import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface Props {
    categoryId: string;
    categoryName: string;
    onSave: () => void;
    onCancel: () => void;
}

export default function PageRequestForm({ categoryId, categoryName, onSave, onCancel }: Props) {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const t = title.trim();
        if (!t) { toast.error("Please enter a page title"); return; }
        setSaving(true);
        axios.post("/api/manuals/page-requests", {
            categoryId,
            title: t,
            body: body.trim() || null,
            note: note.trim() || null,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Page request submitted");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to submit request");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to submit request");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Request a new page</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Admins will see this in the Permission/Access Management queue.
                    </p>
                    <div className="mb-3">
                        <label className="m-label">What page do you want?</label>
                        <input
                            type="text"
                            className="m-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. How to onboard a new client"
                            maxLength={512}
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">What should it cover? (optional)</label>
                        <textarea
                            className="m-input"
                            rows={4}
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            placeholder="Outline the topics or sections you'd like in this page..."
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Anything else? (optional)</label>
                        <textarea
                            className="m-input"
                            rows={2}
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Context, urgency, references..."
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Category</label>
                        <input type="text" className="m-input" value={categoryName} disabled />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Submitting..." : "Submit request"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
